
import Vue from "vue";
import { Dialog } from "@/lib/utils";
import RubyDialog, { RubyDialogProps } from "@/components/ui/RubyDialog.vue";
import EmphasisMarkDialog, { EmphasisMarkDialogProps } from "@/components/ui/EmphasisMarkDialog.vue";
import SearchReplaceDialog from "@/components/ui/SearchReplaceDialog.vue";

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    SearchReplaceDialog,
  },
  props: {
    showSearchReplace: {
      type: Boolean,
      default: true,
    },
    showUndoRedo: {
      type: Boolean,
      default: true,
    },
    showRuby: {
      type: Boolean,
      default: true,
    },
    showEmphasisMark: {
      type: Boolean,
      default: true,
    },
    /** 操作対象の textarea の id。同一ページに複数 textarea がある場合の重複を防ぐ */
    textareaId: {
      type: String,
      default: "textarea",
    },
  },
  computed: {
    textareaElement() {
      return document.getElementById(this.textareaId) as HTMLTextAreaElement;
    },
    enabledSearchReplace() {
      if (!this.showSearchReplace) return false;
      return this.$store.getters["manuscriptModule/enabledSearchReplace"];
    },
  },
  methods: {
    brackets() {
      const { textareaElement } = this;
      if (!textareaElement) return;
      textareaElement.focus();
      const { selectionStart } = textareaElement;
      const selectedText = window.getSelection()!.toString();
      document.execCommand("insertText", false, `「${selectedText}」`);

      // NOTE: 文字選択をしていない場合、括弧の間にカーソルを移動
      if (!selectedText) {
        textareaElement.selectionStart = selectionStart + 1;
        textareaElement.selectionEnd = selectionStart + 1;
      }
    },
    ellipsis() {
      const { textareaElement } = this;
      if (!textareaElement) return;
      textareaElement.focus();
      document.execCommand("insertText", false, "……");
    },
    space() {
      const { textareaElement } = this;
      if (!textareaElement) return;
      textareaElement.focus();
      document.execCommand("insertText", false, "　");
    },
    dash() {
      const { textareaElement } = this;
      if (!textareaElement) return;
      textareaElement.focus();
      document.execCommand("insertText", false, "――");
    },
    async ruby() {
      const { textareaElement } = this;
      if (!textareaElement) return;
      textareaElement.focus();
      const selectedText = window.getSelection()!.toString();

      const rubyDialog = new Dialog(RubyDialog);
      const data: RubyDialogProps = {
        selectedText,
        negative: this.$t("common.cancel") as string,
        positive: this.$t("editorController.dialog.input") as string,
      };
      const result = await rubyDialog.show(data);

      textareaElement.focus();

      if (!result) return;
      document.execCommand("insertText", false, result);
    },
    async emphasisMark() {
      const { textareaElement } = this;
      if (!textareaElement) return;
      textareaElement.focus();
      const selectedText = window.getSelection()!.toString();

      const emphasisMarkDialog = new Dialog(EmphasisMarkDialog);
      const data: EmphasisMarkDialogProps = {
        selectedText,
        negative: this.$t("common.cancel") as string,
        positive: this.$t("editorController.dialog.input") as string,
      };
      const result = await emphasisMarkDialog.show(data);

      textareaElement.focus();

      if (!result) return;
      document.execCommand("insertText", false, result);
    },
    toggleEnabledSearchReplace() {
      if (!this.showSearchReplace) return;
      const { $store, enabledSearchReplace } = this;
      $store.dispatch("manuscriptModule/toggleEnabledSearchReplace");

      if (!enabledSearchReplace) {
        const selectedText = window.getSelection()!.toString();
        if (selectedText) $store.dispatch("manuscriptModule/updateSearchText", selectedText);
      }
    },
    undo() {
      document.execCommand("undo", false, undefined);
    },
    redo() {
      document.execCommand("redo", false, undefined);
    },
  },
});

interface Props {
  showSearchReplace: boolean;
  showUndoRedo: boolean;
  showRuby: boolean;
  showEmphasisMark: boolean;
  textareaId: string;
}

interface Data {}

interface Computed {
  textareaElement: HTMLTextAreaElement | null;
  enabledSearchReplace: boolean;
}

interface Methods {
  brackets(): void;
  ellipsis(): void;
  space(): void;
  dash(): void;
  ruby(): void;
  emphasisMark(): void;
  toggleEnabledSearchReplace(): void;
  undo(): void;
  redo(): void;
}
